import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";

import Message from "./Message";

const PreviewContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1),
  maxHeight: "400px",
  overflowY: "auto",
}));

const InputSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(() => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    fontFamily: "'Courier New', monospace",
  },
}));

const ExampleButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const messageExamples = [
  {
    name: "Code Blocks",
    content:
      // eslint-disable-next-line no-template-curly-in-string -- ${} here is a literal code example, not a real template expression
      'Here are several code blocks in a row:\n\nFirst some JavaScript:\n\n```javascript\nfunction sayHello(name) {\n  const greeting = `Hello, ${name}!`;\n  return greeting;\n}\n```\n\nThen some Python:\n\n```python\ndef say_hello(name):\n  return f"Hello, {name}!"\n```\n\nAnd finally some bash:\n\n```bash\necho "Hello, world!"\n```\n\nNotice how all blocks stay in their original positions.',
  },
  {
    name: "Lists",
    content:
      "Here's a list of items:\n\n1. First item\n2. Second item\n3. Third item\n\nAnd an unordered list:\n\n- Apple\n- Banana\n- Cherry",
  },
  {
    name: "Links",
    content:
      "Check out these links:\n\n- Regular markdown link: [GitHub](https://github.com)\n- Plain URL: https://example.com\n- Another URL inside text: Visit the website at https://www.google.com for more information.",
  },
  {
    name: "Inline Code",
    content:
      "You can use the `console.log()` function to print messages to the console. For example, `console.log('Hello')` will print 'Hello' to the console.",
  },
  {
    name: "Long URLs",
    content:
      "Test how long URLs are displayed: https://this-is-a-very-long-url-that-should-wrap-properly-when-displayed-in-the-message-component.example.com/some/long/path?with=parameters&and=more&parameters=values&test=true",
  },
  {
    name: "Complex Example",
    content:
      // eslint-disable-next-line no-template-curly-in-string -- ${} here is a literal code example, not a real template expression
      "# Testing All Features Together\n\nThis example combines **bold text**, *italics*, and `inline code`.\n\n## Lists\n\n1. First ordered item\n2. Second ordered item\n   - Unordered sub-list\n   - Another item\n\n## Code blocks\n\n```typescript\ninterface User {\n  id: number;\n  name: string;\n  email?: string;\n}\n\nfunction formatUser(user: User): string {\n  return `User: ${user.name} (ID: ${user.id})`;\n}\n```\n\n## Links\n\n- Visit [GitHub](https://github.com)\n- Or just go to https://example.com directly\n\n## Inline code in lists\n\n- Use `npm install` to install dependencies\n- Run `npm start` to start the development server",
  },
];

interface MessageFormatTestProps {
  open: boolean;
  onClose: () => void;
}

const MessageFormatTest: React.FC<MessageFormatTestProps> = ({
  open,
  onClose,
}) => {
  const [messageText, setMessageText] = useState<string>("");
  const [displayedText, setDisplayedText] = useState<string>("");
  const [isUserMessage, setIsUserMessage] = useState<boolean>(false);

  const [activeExample, setActiveExample] = useState<number | null>(null);

  // Update displayed text when message text changes
  useEffect(() => {
    setDisplayedText(messageText);
  }, [messageText]);

  const handleSetExample = (exampleContent: string, index: number) => {
    setMessageText(exampleContent);
    setActiveExample(index);
    setDisplayedText(exampleContent);
  };

  const handleClearText = () => {
    setMessageText("");
    setDisplayedText("");
    setActiveExample(null);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Message Format Test</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" paragraph>
          Test how your messages will look when formatted, without sending any
          API requests. Enter your message text below or select an example to
          see the preview.
        </Typography>

        <InputSection>
          <Typography variant="subtitle2" gutterBottom>
            Example Templates:
          </Typography>
          <Box mb={2} sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {messageExamples.map((example, index) => (
              <ExampleButton
                key={index}
                variant={activeExample === index ? "contained" : "outlined"}
                size="small"
                color={activeExample === index ? "primary" : "inherit"}
                onClick={() => handleSetExample(example.content, index)}
              >
                {example.name}
              </ExampleButton>
            ))}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Button
              size="small"
              color="secondary"
              onClick={handleClearText}
              disabled={!messageText}
            >
              Clear
            </Button>
          </Box>

          <StyledTextField
            label="Message Text"
            multiline
            rows={8}
            variant="outlined"
            value={messageText}
            onChange={(e) => {
              setMessageText(e.target.value);
              setActiveExample(null);
            }}
            placeholder="Type or paste your message here..."
          />

          <Box
            mt={2}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={isUserMessage}
                    onChange={(e) => setIsUserMessage(e.target.checked)}
                    color="primary"
                  />
                }
                label="Show as user message"
              />
            </Box>

            <Typography variant="caption" color="textSecondary">
              {messageText.length} characters
            </Typography>
          </Box>
        </InputSection>

        <Divider />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 2,
            mb: 1,
          }}
        >
          <Typography variant="subtitle1">Message Preview:</Typography>
          <Typography variant="caption" color="textSecondary">
            Shows exactly how your message will appear in the chat
          </Typography>
        </Box>

        <PreviewContainer elevation={1}>
          {messageText ? (
            <>
              (
              <Message
                text={displayedText}
                isUser={isUserMessage}
                timestamp={new Date()}
              />
              )
            </>
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              py={4}
            >
              Enter some text above to see the preview
            </Typography>
          )}
        </PreviewContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MessageFormatTest;
